/**
 * @file upcoming.tsx
 * @module app/modules/advertisement/tabs/upcoming
 * @description Renders the upcoming advertisement tab, presenting campaign records for its corresponding lifecycle state.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useState } from 'react';
import DeleteModal from '../../../modals/delete';
import APICallService from '../../../../api/apiCallService';
import { advertisements } from '../../../../api/apiEndPoints';
import { success } from '../../../../Global/toast';
import { KTSVG } from "../../../../_admin/helpers";
import { CustomSelectTable } from "../../../custom/Select/CustomSelectTable";
import ThreeDotMenu from "../../../../_admin/assets/media/svg_uTrade/three-dot.svg";
import Loader from "../../../../Global/loader";
import { AdvertisementUpcoming, Advertising, HomePage, View } from "../../../../utils/constants";
import Method from "../../../../utils/methods";
import Pagination from "../../../../Global/pagination";
import { listAdvertisement } from "../../../../utils/storeString";
import { setKey } from "../../../../Global/history";
import { useAuth } from '../../auth';
import { useNavigationHistory } from "../../../../utils/useNavigationHistory";
import ImageWithFallback from "../../_common/components/image-with-fallback";
const Upcoming = (props: any) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAd, setSelectedAd] = useState<any>(null);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [error, setError] = useState('');
  const [menuShow, setMenuShow] = useState(false);
  const [menuId, setMenuId] = useState<number>(-1);

  const openMenuOnClick = async (id: any) => {
    setMenuId(id);
    setMenuShow(true);
  };
  const onMenuClose = async () => {
    setMenuId(-1);
    setMenuShow(false);
  };
  const onMenuOpen = async (id: any) => {
    setMenuId(id);
    setMenuShow(true);
  };

  const handleOption = async (event: any, index: number, data: any) => {
    if (!event) return;
    if (event.value === 2 || event.value === 'delete') {
      setSelectedAd(data._id || data);
      setShowDeleteModal(true);
    }
  };

  const handleDelete = async () => {
    setDeleteLoader(true);
    let apiService = new APICallService(
      advertisements.deleteAdvertisement,
      selectedAd,
      "",
      "",
      "",
      true,
      Advertising
    );
    let response = await apiService.callAPI();
    if (response && response.error) {
      setError(response.error);
    } else {
      success('Advertisement deleted successfully');
      setShowDeleteModal(false);
      props.handleCurrentPage && props.handleCurrentPage(props.currentPage, AdvertisementUpcoming);
    }
    setDeleteLoader(false);
  };

  const { navigateTo } = useNavigationHistory();
  return (
    <>
      {/* begin::Search */}
      {/* <div className="position-relative my-1">
        <KTSVG
          path="/media/icons/duotune/general/gen021.svg"
          className="svg-icon-3 svg-icon-gray-500 position-absolute top-50 translate-middle ps-13"
        />
        <input
          type="text"
          className="form-control form-control-custom borde-r8px bg-light w-375px ps-11"
          name="Search Team"
          onChange={(event: any) => {
            props.handleSearch(event, AdvertisementUpcoming);
          }}
          placeholder="Search by seller name…"
        />
      </div> */}
      {/* end::Search */}
      <div className="table-responsive">
        <table className="table table-rounded table-row-bordered align-middle gy-4 mb-0">
          <thead>
            <tr className="fw-bold fs-16 fw-600 text-dark border-bottom h-70px align-middle">
              <th className="min-w-125px">Banner</th>
              <th className="min-w-150px">Vendor</th>
              <th className="min-w-160px">Ad placement</th>
              <th className="min-w-125px">IsPharmacy</th>
              <th className="min-w-160px">Start date</th>
              <th className="min-w-160px">Cost per day</th>
              <th className="min-w-175px"></th>
            </tr>
          </thead>
          <tbody>
            {props.fetchLoading ? (
              <>
                <tr>
                  <td colSpan={7}>
                    <div className="d-flex justify-content-center">
                      <Loader loading={props.fetchLoading} />
                    </div>
                  </td>{" "}
                </tr>
              </>
            ) : (
              <>
                {props.advertisement.length ? (
                  <>
                    {props.advertisement.map(
                      (advertiseVal: any, index: number) => {
                        return (
                          <>
                            <tr>
                              <td>
                                <ImageWithFallback
                                  className="img-fluid mw-87px mh-50px border-r6px object-fit-contain"
                                  src={advertiseVal.image}
                                  alt=""
                                  noDefaultSize
                                />
                              </td>
                              <td>
                                <span className="fs-15 fw-500">
                                  {advertiseVal.business.ownerName}
                                </span>
                              </td>
                              <td>
                                <span className="fs-15 fw-500">
                                  {advertiseVal.placement === HomePage
                                    ? "Home page"
                                    : "Category page"}
                                </span>
                              </td>
                              <td>
                                <span className="fs-15 fw-500">
                                  {advertiseVal.isPharmacyAd ? 'Yes' : 'No'}
                                </span>
                              </td>
                              <td>
                                <span className="fs-15 fw-500">
                                  {" "}
                                  {Method.convertDateToDDMMYYYY(
                                    advertiseVal.startDate
                                  )}
                                </span>
                              </td>
                              <td>
                                <span className="fs-15 fw-500">
                                  TSh{" "}
                                  {Method.getGeneralizedAmount(
                                    advertiseVal.amount
                                  )}
                                </span>
                              </td>
                              <td>
                                <div className="d-flex gap-4">
                                {Method.hasPermission(Advertising, View, currentUser) ? (
                                  <Button
                                    variant="primary"
                                    className="uw-btn uw-btn-primary btn-active-light-primary fs-14 fw-600"
                                    data-analytics-event="advertisement_upcoming_view_details_clicked"
                                    onClick={() => {
                                      let currentScrollY = window.scrollY;
                                      setKey(
                                        listAdvertisement.scrollPosition,
                                        currentScrollY
                                      );
                                      navigateTo(
                                        "/advertisement/view-advertisement/advertising-details",
                                        {
                                          state: {
                                            status: AdvertisementUpcoming,
                                            id: advertiseVal._id,
                                          },
                                        }
                                      );
                                    }}
                                  >
                                    View details
                                  </Button>
                                  ) : (
                                    <></>
                                  )}
                                  <div className="d-inline-block">
                                    <CustomSelectTable
                                      marginLeft={'0px'}
                                      width={'130px'}
                                      placeholder={
                                        <img
                                          className="img-fluid"
                                          width={18}
                                          height={5}
                                          src={ThreeDotMenu}
                                          alt=""
                                        />
                                      }
                                      backgroundColor="white"
                                      options={[
                                        {
                                          label: (
                                            <button className="btn btn-link fs-14 fw-500 text-danger ms-3 p-4" data-analytics-event="advertisement_upcoming_delete_clicked">
                                              Delete
                                            </button>
                                          ),
                                          value: 2,
                                        },
                                      ]}
                                      show={menuShow && index === menuId}
                                      onMenuClose={onMenuClose}
                                      openMenuOnClick={() => openMenuOnClick(index)}
                                      onMenuOpen={() => onMenuOpen(index)}
                                      onChange={(event: any) =>
                                        handleOption(event, index, advertiseVal)
                                      }
                                    />
                                  </div>
                                </div>
                              </td>
                            </tr>
                          </>
                        );
                      }
                    )}
                  </>
                ) : (
                  <tr>
                    <td colSpan={7}>
                      <div className="d-flex justify-content-center">
                        <span className="fs-15 fw-500">No Data found</span>
                      </div>
                    </td>{" "}
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
      {props.fetchLoading ? (
        <></>
      ) : (
        <>
          {props.advertisement.length ? (
            <>
              <Pagination
                totalRecords={props.total}
                currentPage={props.currentPage}
                handleCurrentPage={(event: any) => {
                  props.handleCurrentPage(event, AdvertisementUpcoming);
                }}
                handleNextPage={(event: any) => {
                  props.handleNextPage(event, AdvertisementUpcoming);
                }}
                handlePreviousPage={(event: any) => {
                  props.handlePreviousPage(event, AdvertisementUpcoming);
                }}
                pageLimit={parseInt(props.pageLimit)}
                handlePageLimit={(event: any) => {
                  props.handlePageLimit(event, AdvertisementUpcoming);
                }}
              />
            </>
          ) : (
            <></>
          )}
        </>
      )}
      {showDeleteModal && (
        <DeleteModal
          show={showDeleteModal}
          onHide={() => {
            setShowDeleteModal(false);
            setSelectedAd(null);
            setError('');
          }}
          handleDelete={handleDelete}
          error={error}
          loading={deleteLoader}
          serviceName="advertisement"
          message={`Are you sure you want to delete this advertisement?`}
          warnMessage={`By continuing, the advertisement will be removed permanently`}
        />
      )}
      </>
  );
};
export default Upcoming;
